"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateDoctorSchema = exports.createDoctorSchema = exports.doctorShape = exports.doctorConsultationTypeEnum = exports.doctorEmploymentTypeEnum = void 0;
const zod_1 = require("zod");
const employee_master_schema_1 = require("./employee-master.schema");
// Doctor Master enterprise rebuild. `additionalSpecialtyDepartmentIds`
// covers every specialty beyond the primary `departmentId` — see the
// `DoctorSpecialty` Prisma model's own doc comment for why this is a
// real join table, not a free-text label.
exports.doctorEmploymentTypeEnum = zod_1.z.enum(['FULL_TIME', 'PART_TIME', 'VISITING', 'CONSULTANT', 'RESIDENT']);
exports.doctorConsultationTypeEnum = zod_1.z.enum(['OP', 'IP', 'EMERGENCY', 'TELE_CONSULTATION']);
exports.doctorShape = {
    doctorCode: zod_1.z.string().max(30).optional(),
    name: zod_1.z.string().min(1).max(255),
    displayName: zod_1.z.string().max(255).nullable().optional(),
    title: zod_1.z.string().max(50).nullable().optional(),
    gender: employee_master_schema_1.employeeGenderEnum.nullable().optional(),
    departmentId: zod_1.z.string().uuid(),
    additionalSpecialtyDepartmentIds: zod_1.z.array(zod_1.z.string().uuid()).optional(),
    speciality: zod_1.z.string().max(150).nullable().optional(),
    subSpeciality: zod_1.z.string().max(150).nullable().optional(),
    designationId: zod_1.z.string().uuid().nullable().optional(),
    qualification: zod_1.z.string().max(255).nullable().optional(),
    registrationNumber: zod_1.z.string().max(100).nullable().optional(),
    experienceYears: zod_1.z.number().int().min(0).nullable().optional(),
    mobileNumber: zod_1.z.string().max(20).nullable().optional(),
    /**
     * An empty string is treated as "not provided", not as an invalid address.
     *
     * Without this, a form that resets a blank field to `''` fails
     * `z.string().email()` and the submit is silently refused — which is exactly
     * what happened in Doctor Master: every doctor with no email address could
     * not be saved, with no error shown against any field.
     */
    contactEmail: zod_1.z
        .preprocess((value) => (value === '' ? undefined : value), zod_1.z.string().email().max(255).nullable().optional()),
    employmentType: exports.doctorEmploymentTypeEnum.optional(),
    consultationTypes: zod_1.z.array(exports.doctorConsultationTypeEnum).optional(),
    isAppointmentEnabled: zod_1.z.boolean().optional(),
    /**
     * How many appointment slots this doctor takes per day. Bookings number
     * 1..maxSlotsPerDay and that number is the queue position.
     *
     * Only meaningful when `isAppointmentEnabled` — the form hides it otherwise —
     * but not conditionally required here: a doctor can be temporarily switched
     * off for appointments without losing their configured capacity.
     */
    maxSlotsPerDay: zod_1.z.number().int().min(1).max(500).optional(),
    /**
     * ── The consultation, and whose money it is ──────────────────────────
     *
     * The head this doctor's consultation is billed to. Chosen from the OP
     * consultation heads of the doctor's department — resolved through
     * `Department.accountingDepartmentId`, because 7 of the 24
     * appointment-enabled departments own no heads of their own.
     *
     * Nullable in the schema and REQUIRED AT SAVE for an appointment-enabled
     * doctor — a rule in `DoctorMasterService`, not here, because it depends
     * on the row's other values and a field-level rule cannot see them.
     */
    consultationAccountHeadId: zod_1.z.string().uuid().nullable().optional(),
    /** In PAISE. Integer, like every money value that crosses this boundary. */
    consultationFeePaise: zod_1.z.number().int().min(0).max(100_000_000).nullable().optional(),
    /** PERCENT reads `doctorShareValue` as basis points; AMOUNT reads it as paise. */
    doctorShareMode: zod_1.z.enum(['PERCENT', 'AMOUNT']).nullable().optional(),
    /**
     * Basis points (2500 = 25.00%) or paise, per `doctorShareMode`. Capped at
     * 10000 bp = 100%: a consultant cannot be owed more than was collected.
     * The hospital's share is the remainder and is never sent or stored.
     */
    doctorShareValue: zod_1.z.number().int().min(0).max(100_000_000).nullable().optional(),
    isIpEnabled: zod_1.z.boolean().optional(),
    isOtEnabled: zod_1.z.boolean().optional(),
    isEmergencyEnabled: zod_1.z.boolean().optional(),
    displayOrder: zod_1.z.number().int().min(0).optional(),
    isActive: zod_1.z.boolean().optional(),
};
exports.createDoctorSchema = zod_1.z.object(exports.doctorShape);
exports.updateDoctorSchema = exports.createDoctorSchema.partial();
//# sourceMappingURL=doctor-master.schema.js.map